import { useEffect } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";

/**
 * Hook to listen for assignment updates and trigger refetch + notification
 * @param {Object} params
 * @param {string} params.modelType - Type of model (e.g., "Interpretation", "Translation", "OtherService")
 * @param {number} params.recordId - ID of the assignment/record
 * @param {Function} params.onUpdate - Callback function to refetch data (called when update is received, receives updatedBy object)
 * @param {Function} params.toastFn - Toast notification function (optional, for custom toast handling)
 * @returns {void}
 */
export const useAssignmentUpdates = ({
  modelType,
  recordId,
  onUpdate,
  toastFn,
}) => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const { showNotification } = useStateContext();

  useEffect(() => {
    if (!socket || !modelType || !recordId || !onUpdate) {
      return;
    }

    // Handler for assignment updates
    const handleAssignmentUpdate = ({
      updatedBy,
      modelType: updateModelType,
      recordId: updateRecordId,
      projectNumber,
      changeType,
      changeAction,
    }) => {
      const recordIdMatch = updateRecordId === recordId;
      const recordIdNumberMatch = Number(updateRecordId) === Number(recordId);

      // Only process if this is for our current assignment
      if (
        updateModelType === modelType &&
        (recordIdMatch || recordIdNumberMatch)
      ) {
        // Don't show notification to the user who made the update
        if (updatedBy.userId !== currentUser?.id) {
          const meta = {
            projectNumber,
            changeType,
            changeAction,
            modelType: updateModelType,
            recordId: updateRecordId,
          };

          // Show toast notification
          if (toastFn) {
            toastFn(
              `${updatedBy.userName} updated the assignment. Check History for more details.`,
            );
          } else if (showNotification) {
            showNotification({
              type: "info",
              message: `${updatedBy.userName} updated the assignment. Check History for more details.`,
            });
          }

          // Trigger refetch with updater info
          onUpdate(updatedBy, meta);
        }
      }
    };

    // Listen for assignment:updated events
    socket.on("assignment:updated", handleAssignmentUpdate);

    // Cleanup
    return () => {
      socket.off("assignment:updated", handleAssignmentUpdate);
    };
  }, [
    socket,
    modelType,
    recordId,
    onUpdate,
    currentUser?.id,
    showNotification,
    toastFn,
  ]);
};

export default useAssignmentUpdates;
